import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import { showError, showSuccess } from '../../../../core/alert/sweetAlertConfig';
import { all_routes } from '../../../router/all_routes';
import CollapseHeader from '../../../../core/common/collapse-header';

/** DTOs */
interface LocationDTO {
  id: number;
  name: string;
  address: string;
  lat: number;
  lng: number;
  description?: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}
interface BuildingDTO {
  id: number;
  name: string;
  noOfFloors: number;
  locationName: string;
  locationId: number;
  description?: string | null;
}
interface FloorDTO {
  id: number;
  name: string;
  floorNumber: number;
  buildingId: number;
  buildingName: string;
  locationName: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}
interface KeyDTO {
  id: string;
  keyCode: string;
  buildingId: number;
  buildingName: string | null;
  floorId: number;
  floorName: string | null;
  roomId: number | null;
  roomName: string | null;
  doorId: string;
  noOfKeys: number;
  vehicleId: string | null;
}

interface GetLocationsResponse {
  locations: LocationDTO[];
  total: number;
}
interface GetAvailableKeysResponse {
  keys: KeyDTO[];
  total: number;
}

/** Name with numeric-first sort */
const numericFirstSort = (a: string, b: string) => {
  const na = parseInt(a.replace(/\D+/g, ''), 10);
  const nb = parseInt(b.replace(/\D+/g, ''), 10);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && na !== nb) return na - nb;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
};

const KeyRequest: React.FC = () => {
  const locationHook = useLocation();
  const navigate = useNavigate();

  const [locations, setLocations] = useState<LocationDTO[]>([]);
  const [buildings, setBuildings] = useState<BuildingDTO[]>([]);
  const [floors, setFloors] = useState<FloorDTO[]>([]);
  const [availableKeys, setAvailableKeys] = useState<KeyDTO[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<KeyDTO[]>([]);
  const [reason, setReason] = useState<string>('');
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null);
  const [selectedFloorId, setSelectedFloorId] = useState<number | null>(null);
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);
  const [filterType, setFilterType] = useState<string>('BUILDING');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showError('Authentication token not found. Please log in.');
      navigate('/login');
      return false;
    }
    try {
      const decoded = JSON.parse(atob(token.split('.')[1]));
      const exp = decoded.exp * 1000; // Convert to milliseconds
      if (Date.now() >= exp) {
        showError('Session expired. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
        return false;
      }
    } catch (e) {
      showError('Invalid authentication token. Please log in again.');
      localStorage.removeItem('token');
      navigate('/login');
      return false;
    }
    return token;
  };

  // Function to get userId from token or localStorage
  const getUserId = () => {
    const token = localStorage.getItem('token');
    const userIdFromStorage = localStorage.getItem('userId');
    if (userIdFromStorage) {
      return userIdFromStorage;
    }
    if (token) {
      try {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        return decoded.userId || decoded.sub || null; // Adjust based on token structure
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  /** Initial: locations */
  useEffect(() => {
    const run = async () => {
      if (!checkToken()) return;
      if (!checkPermission('ROLE_GET_LOCATIONS')) {
        showError('You do not have permission to view locations.');
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const payload = { filterType: '', filterValue: '', date: '' };
        const res: AxiosResponse = await apiDefinitions.getLocations('1', payload);
        if (res.status === 200) {
          const data: GetLocationsResponse = res.data.data;
          setLocations(data.locations || []);
          if ((data.locations || []).length === 0) setError('No locations found. Please add a location first.');
        } else {
          showError(res.data?.statusDescription || 'Failed to fetch locations');
        }
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        } else showError('Failed to fetch locations. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [navigate]);

  /** When location changes → buildings */
  useEffect(() => {
    const run = async () => {
      if (!selectedLocationId) {
        setBuildings([]);
        setSelectedBuildingId(null);
        setFloors([]);
        setAvailableKeys([]);
        setSelectedFloorId(null);
        setSelectedRoomId(null);
        setSelectedKeys([]);
        setFilterType('BUILDING');
        return;
      }
      if (!checkToken()) return;
      if (!checkPermission('ROLE_GET_LOCATIONS')) {
        showError('You do not have permission to view buildings.');
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const res: AxiosResponse = await apiDefinitions.getBuildingsByLocationId(selectedLocationId);
        if (res.status === 200) {
          const data: BuildingDTO[] = res.data.data;
          setBuildings(data || []);
          setSelectedBuildingId(null);
          setFloors([]);
          setAvailableKeys([]);
          setSelectedFloorId(null);
          setSelectedRoomId(null);
          setSelectedKeys([]);
          setFilterType('BUILDING');
          if ((data || []).length === 0) setError(`No buildings found for location ID ${selectedLocationId}.`);
        } else {
          showError(res.data?.statusDescription || 'Failed to fetch buildings');
        }
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        } else showError('Failed to fetch buildings. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [selectedLocationId, navigate]);

  /** When building or floor changes → fetch floors + available keys */
  useEffect(() => {
    const run = async () => {
      if (!selectedBuildingId) {
        setFloors([]);
        setAvailableKeys([]);
        setSelectedFloorId(null);
        setSelectedRoomId(null);
        setSelectedKeys([]);
        setFilterType('BUILDING');
        return;
      }
      if (!checkToken()) return;

      setIsLoading(true);
      setError(null);
      try {
        if (!checkPermission('ROLE_VIEW_FLOOR')) {
        }
        if (!checkPermission('ROLE_GET_KEYS')) {
          showError('You do not have permission to view keys.');
          setIsLoading(false);
          return;
        }

        let floorsRes: AxiosResponse | null = null;
        if (checkPermission('ROLE_VIEW_FLOOR')) {
          floorsRes = await apiDefinitions.getFloorsByBuildingId(selectedBuildingId);
        }

        let filterValue = String(selectedBuildingId);
        if (filterType === 'FLOOR' && selectedFloorId) {
          filterValue = String(selectedFloorId);
        }
        const keysRes: AxiosResponse = await apiDefinitions.getAvailableKeys(1, { filterType, filterValue });

        if (floorsRes && floorsRes.status === 200) {
          const fData: FloorDTO[] = floorsRes.data.data || [];
          const sortedFloors = fData.sort((a, b) => {
            if (a.floorNumber === 0) return -1;
            if (b.floorNumber === 0) return 1;
            return a.floorNumber - b.floorNumber;
          });
          setFloors(sortedFloors);
          if (sortedFloors.length > 0 && !selectedFloorId && filterType !== 'BUILDING') {
            setSelectedFloorId(sortedFloors[0].id);
            setFilterType('FLOOR');
          }
          if (sortedFloors.length === 0) {
            setError(`No floors found for building ID ${selectedBuildingId}.`);
          }
        } else if (floorsRes) {
          setError('Failed to fetch floors. Displaying keys for the selected building.');
        }

        if (keysRes.status === 200) {
          const kData: GetAvailableKeysResponse = keysRes.data.data;
          const sortedKeys = (kData.keys || []).slice().sort((a, b) => numericFirstSort(a.keyCode, b.keyCode));
          setAvailableKeys(sortedKeys);
          if ((kData.keys || []).length === 0) setError(`No available keys found for ${filterType.toLowerCase()} ID ${filterValue}.`);
        } else {
          showError(keysRes.data?.statusDescription || 'Failed to fetch available keys');
        }
      } catch (e: any) {
        if (e.response?.status === 401 && e.response?.data?.status !== 'E1001') {
          localStorage.removeItem('token');
          navigate('/login');
        } else {
          setError(e.response?.data?.statusDescription || 'Failed to fetch floors/keys. Displaying available keys.');
          if (e.response?.config?.url?.includes('key-allocation/available-keys')) {
            setAvailableKeys([]);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [selectedBuildingId, selectedFloorId, filterType, navigate]);

  const selectedLocationName = useMemo(
    () => (selectedLocationId ? (locations.find(l => l.id === selectedLocationId)?.name || '') : ''),
    [selectedLocationId, locations]
  );
  const selectedBuildingName = useMemo(
    () => (selectedBuildingId ? (buildings.find(b => b.id === selectedBuildingId)?.name || '') : ''),
    [selectedBuildingId, buildings]
  );

  const rooms = useMemo(() => {
    const roomMap = new Map<number, { id: number; name: string }>();
    availableKeys.forEach(key => {
      if (key.roomId && key.roomName) {
        roomMap.set(key.roomId, { id: key.roomId, name: key.roomName });
      }
    });
    return Array.from(roomMap.values()).sort((a, b) => numericFirstSort(a.name, b.name));
  }, [availableKeys]);

  const currentDateTime = new Date().toLocaleString('en-US', {
    timeZone: 'Asia/Kolkata',
    hour12: true,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const toggleKeySelection = (key: KeyDTO) => {
    setSelectedKeys(prev =>
      prev.some(k => k.id === key.id)
        ? prev.filter(k => k.id !== key.id)
        : [...prev, key]
    );
  };

  const removeFromCart = (keyId: string) => {
    setSelectedKeys(prev => prev.filter(k => k.id !== keyId));
  };

  const handleRequestKeys = async () => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_REQUEST_KEY')) {
      showError('You do not have permission to request keys.');
      return;
    }
    if (selectedKeys.length === 0) {
      showError('Please select at least one key to request.');
      return;
    }
    if (!reason.trim()) {
      showError('Please provide a reason for the key request.');
      return;
    }
    const userId = getUserId();
    if (!userId) {
      showError('User ID not found. Please log in again.');
      navigate('/login');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const payload = {
        keyIds: selectedKeys.map(key => Number(key.id)),
        reason: reason.trim(),
        requestorId: userId, // Add requestorId to the payload
      };
      const res: AxiosResponse = await apiDefinitions.requestKey(payload);
      if (res.status === 200) {
        showSuccess("Key request submitted successfully");
        setSelectedKeys([]);
        setReason('');
        const filterValue = filterType === 'FLOOR' && selectedFloorId ? String(selectedFloorId) : String(selectedBuildingId);
        const keysRes: AxiosResponse = await apiDefinitions.getAvailableKeys(1, { filterType, filterValue });
        if (keysRes.status === 200) {
          const kData: GetAvailableKeysResponse = keysRes.data.data;
          const sortedKeys = (kData.keys || []).slice().sort((a, b) => numericFirstSort(a.keyCode, b.keyCode));
          setAvailableKeys(sortedKeys);
        }
      } else {
        showError(res.data?.statusDescription || 'Failed to submit key request.');
      }
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        showError(e.response?.data?.statusDescription || 'Failed to submit key request. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const KeyCard: React.FC<{ keyItem: KeyDTO }> = ({ keyItem }) => (
    <div className="card h-100 border border-2 shadow-sm transition-all hover:shadow-md">
      <div className="card-body p-2 d-flex align-items-center">
        <div className="form-check mb-0 me-2">
          <input
            type="checkbox"
            className="form-check-input"
            checked={selectedKeys.some(k => k.id === keyItem.id)}
            onChange={() => toggleKeySelection(keyItem)}
          />
        </div>
        <div className="flex-grow-1">
          <span className="fw-semibold text-dark">{keyItem.keyCode}</span>
          <small className="d-block text-muted">({keyItem.noOfKeys} keys)</small>
        </div>
        <small className="text-muted text-end">{keyItem.roomName || 'No Room'}</small>
      </div>
    </div>
  );

  return (
    <div className="page-wrapper">
      <style>{`
        .transition-all {
          transition: all 0.2s ease-in-out;
        }
        .hover\\:shadow-md:hover {
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        .form-select, .form-control, .form-control-lg {
          border-radius: 0.375rem;
          box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.075);
        }
        .dropdown-menu {
          max-height: 200px;
          overflow-y: auto;
          border-radius: 0.375rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        .dropdown-item:hover {
          background-color: #f8f9fa;
        }
        .card {
          border-radius: 0.5rem;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }
        .btn-primary {
          background-color: #007bff;
          border-color: #007bff;
          transition: background-color 0.2s;
        }
        .btn-primary:hover {
          background-color: #0056b3;
          border-color: #004085;
        }
        .btn-danger {
          transition: background-color 0.2s;
        }
        .btn-danger:hover {
          background-color: #c82333;
          border-color: #bd2130;
        }
        .table th, .table td {
          vertical-align: middle;
        }
      `}</style>

      <div className="content container-fluid py-4">
        <div className="row">
          <div className="col-12">
            <div className="page-header mb-4">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Key Management/Key Requests</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body p-4">
                <div className="row g-3 mb-4">
                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Location</label>
                    <select
                      className="form-select"
                      value={selectedLocationId || ''}
                      onChange={(e) => setSelectedLocationId(e.target.value ? Number(e.target.value) : null)}
                      disabled={isLoading}
                    >
                      <option value="">{locations.length === 0 ? 'No locations available' : 'Select a location'}</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Building</label>
                    <select
                      className="form-select"
                      value={selectedBuildingId || ''}
                      onChange={(e) => setSelectedBuildingId(e.target.value ? Number(e.target.value) : null)}
                      disabled={isLoading || !selectedLocationId}
                    >
                      <option value="">{buildings.length === 0 ? 'No buildings available' : 'Select a building'}</option>
                      {buildings.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Floor</label>
                    <select
                      className="form-select"
                      value={selectedFloorId || ''}
                      onChange={(e) => {
                        const floorId = e.target.value ? Number(e.target.value) : null;
                        setSelectedFloorId(floorId);
                        setFilterType(floorId ? 'FLOOR' : 'BUILDING');
                        setSelectedRoomId(null);
                      }}
                      disabled={isLoading || !selectedBuildingId || floors.length === 0}
                    >
                      <option value="">{floors.length === 0 ? 'No floors available' : 'Select a floor'}</option>
                      {floors.map(f => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Room</label>
                    <select
                      className="form-select"
                      value={selectedRoomId || ''}
                      onChange={(e) => setSelectedRoomId(e.target.value ? Number(e.target.value) : null)}
                      disabled={isLoading || !selectedBuildingId || rooms.length === 0}
                    >
                      <option value="">{rooms.length === 0 ? 'No rooms available' : 'Select a room'}</option>
                      {rooms.map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {isLoading && (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {error}
                    <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
                  </div>
                )}

                {selectedLocationId && selectedBuildingId && availableKeys.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-center mb-4 fw-semibold">
                      {selectedLocationName} - 🏢 {selectedBuildingName}
                      {selectedFloorId && ` - Floor: ${floors.find(f => f.id === selectedFloorId)?.name || ''}`}
                      {selectedRoomId && ` - Room: ${rooms.find(r => r.id === selectedRoomId)?.name || ''}`}
                    </h4>

                    <div className="container-fluid">
                      <h5 className="mb-3 fw-semibold">Available Keys</h5>
                      <div className="row g-3">
                        {availableKeys
                          .filter(key => !selectedRoomId || key.roomId === selectedRoomId)
                          .map(key => (
                            <div key={key.id} className="col-6 col-sm-4 col-md-3">
                              <KeyCard keyItem={key} />
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="row g-3">
                  <div className="col-md-8">
                    <label className="form-label fw-semibold">Reason for Request</label>
                    <textarea
                      className="form-control form-control-lg"
                      rows={4}
                      placeholder="Enter the reason for requesting the keys"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      disabled={isLoading}
                    ></textarea>
                  </div>
                  <div className="col-md-4 d-flex align-items-end">
                    <button
                      className="btn btn-success w-100"
                      onClick={handleRequestKeys}
                      disabled={isLoading || selectedKeys.length === 0 || !reason.trim()}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-check me-2"></i> Request Keys
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {selectedKeys.length > 0 && (
                  <div className="card mt-4">
                    <div className="card-header bg-light">
                      <h5 className="card-title mb-0">Selected Keys ({selectedKeys.length})</h5>
                    </div>
                    <div className="card-body p-4">
                      <div className="table-responsive mb-4">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>Key Code</th>
                              <th>Number of Keys</th>
                              <th>Room</th>
                              <th>Building</th>
                              <th>Floor</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedKeys.map(key => (
                              <tr key={key.id}>
                                <td>{key.keyCode}</td>
                                <td>{key.noOfKeys}</td>
                                <td>{key.roomName || 'No Room'}</td>
                                <td>{key.buildingName || 'N/A'}</td>
                                <td>{key.floorName || 'N/A'}</td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => removeFromCart(key.id)}
                                  >
                                    <i className="ti ti-trash me-1"></i> Remove
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyRequest;